// Layer System Types
// Defines the types for the layer management system

/**
 * Layer type enumeration
 * Represents all possible layer types in the diagram
 */
export enum LayerType {
  ITEM = 'ITEM',
  CONNECTOR = 'CONNECTOR',
  RECTANGLE = 'RECTANGLE',
  TEXTBOX = 'TEXTBOX',
}

/**
 * Base layer properties that all layers share
 */
export interface BaseLayerProperties {
  /** Whether the layer is visible */
  visible?: boolean;
  /** Whether the layer is locked (prevents interaction) */
  locked?: boolean;
}

/**
 * Layer metadata with type information
 */
export interface LayerMetadata extends BaseLayerProperties {
  /** Unique identifier for the layer */
  id: string;
  /** Type of the layer */
  type: LayerType;
  /** Display name for the layer */
  name: string;
  /** Z-index for layer ordering (higher = on top) */
  zIndex?: number;
}

/**
 * Layer group metadata
 */
export interface LayerGroup {
  /** Unique identifier for the group */
  id: string;
  /** Display name for the group */
  name: string;
  /** Whether the group is collapsed in the UI */
  collapsed?: boolean;
  /** Layer IDs in this group */
  layerIds: string[];
  /** Whether the entire group is visible */
  visible?: boolean;
  /** Whether the entire group is locked */
  locked?: boolean;
}

/**
 * Layer action types
 */
export interface LayerActions {
  /** Toggle layer visibility */
  toggleLayerVisibility: (layerId: string) => void;
  /** Set layer visibility */
  setLayerVisibility: (layerId: string, visible: boolean) => void;
  /** Toggle layer locked state */
  toggleLayerLocked: (layerId: string) => void;
  /** Set layer locked state */
  setLayerLocked: (layerId: string, locked: boolean) => void;
  /** Show all layers */
  showAllLayers: () => void;
  /** Hide all layers */
  hideAllLayers: () => void;
  /** Lock all layers */
  lockAllLayers: () => void;
  /** Unlock all layers */
  unlockAllLayers: () => void;
  /** Delete a layer */
  deleteLayer: (layerId: string) => void;
  /** Duplicate a layer */
  duplicateLayer: (layerId: string) => void;
}

/**
 * Layer state for the current view
 */
export interface LayerState {
  /** All layers in the current view */
  layers: LayerMetadata[];
  /** Layer groups (optional, for future expansion) */
  groups?: LayerGroup[];
  /** Currently selected layer IDs */
  selectedLayerIds: string[];
}
